// apps/vendor/src/lib/i18n/index.ts
//
// Phase 7 Wave 18 Task 76 (Amendment #1) — public i18n entry point for the
// vendor SPA. Initialises i18next on first import and re-exports the React
// hooks + canonical locale list used by the TopBar selector.
//
// Mirrors the `apps/merchant-v3/src/lib/i18n/index.ts` shape so future
// portals can copy this file with minimal edits.
//
// vg-binding: PLAN:task-76
// vg-binding: P7.D-134
// vg-binding: P7.D-152
// vg-binding: AMENDMENT-1

import { useTranslation, Trans, I18nextProvider } from 'react-i18next';

import {
  i18next,
  initI18n,
  LOCALES,
  NAMESPACES,
  userLangToLocale,
  localeToUserLang,
} from './config.js';
import type { Locale } from './config.js';
import { I18nProvider } from './I18nProvider.js';

// Boot once at module load — safe because config.ts guards re-entry.
const i18n = initI18n();

/** Public language code used by the user-store / UI components. */
export type SupportedLanguage = 'EN' | 'VI' | 'FR';

/** Mirrors the merchant-v3 public API; consumed by the TopBar selector. */
export const SUPPORTED_LANGUAGES: ReadonlyArray<{
  code: SupportedLanguage;
  label: string;
  flag: string;
}> = LOCALES.map((l) => ({ code: l.user, label: l.label, flag: l.flag }));

/**
 * Synchronous translate — safe to call outside React (e.g. error toasts
 * fired from Zustand selectors). Inside components prefer `useTranslation()`
 * which subscribes to language changes.
 */
export function t(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, options) as string;
}

/** Switch active language; persists via the cookie + localStorage detector. */
export function changeLanguage(lang: SupportedLanguage): Promise<unknown> {
  return i18n.changeLanguage(userLangToLocale(lang));
}

export {
  i18n,
  i18next,
  useTranslation,
  Trans,
  I18nextProvider,
  I18nProvider,
  LOCALES,
  NAMESPACES,
  userLangToLocale,
  localeToUserLang,
};
export type { Locale };
